/*
 * Data layer events (VIS-921).
 *
 * The theme pushes every storefront event onto the GTM dataLayer with the
 * complete object the Storeis SDD asks for: pageview, view_item, add_to_cart
 * (from product forms and from quantity increases in the cart), view_cart,
 * view_item_list, preferred_store_set and book_appointment (step 0, and step 3
 * on the manual booking request form). GTM stays
 * in the theme (snippets/google-tag-manager-head.liquid); the custom web pixel
 * covers only checkout and the thank-you page, where the theme does not run.
 * Split agreed with Storeis on 2026-09-17; contract for the pixel in
 * tools/web-pixel/README.md, gotchas in AGENTS.md "Data layer events".
 *
 * Item objects come from Liquid (`snippets/analytics-item.liquid`) through data
 * attributes and JSON script tags, so no Storefront API call happens here.
 */

import { ThemeEvents } from '@theme/events';
import { getFavorite } from '@theme/store-favorites';
import { debounce, isMobileBreakpoint, parseJSON, rememberItemList, rememberedItemList } from '@theme/utilities';

const LIST_PROPERTY = 'properties[_item_list_name]';
const ITEM_PROPERTY = 'properties[_analytics_item]';
const BATCH_SIZE = 10;
const FLUSH_DELAY_MS = 50;
const mediaQueryDesktop = matchMedia('(min-width: 990px)');

/**
 * Single exit point. Ecommerce objects are preceded by the GA4 reset so a
 * previous event's items never leak into the next one.
 * @param {Record<string, unknown>} payload
 */
function push(payload) {
  window.dataLayer = window.dataLayer || [];
  if (payload.ecommerce) window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(payload);
}

/**
 * `Theme` is a script-scope `const` in snippets/scripts.liquid: a global
 * binding, not a `window` property, so read the bare identifier.
 * @returns {Record<string, any>}
 */
function pageContext() {
  // eslint-disable-next-line no-undef
  return (typeof Theme !== 'undefined' && Theme.analytics) || {};
}

/* -------------------------------- pageview -------------------------------- */

/** @returns {string} */
function responsiveLayout() {
  if (isMobileBreakpoint()) return 'mobile';
  return mediaQueryDesktop.matches ? 'desktop' : 'tablet';
}

/** @returns {string} The active `filter.*` params, sorted; empty when unfiltered. */
function activeFiltersKey() {
  return [...new URLSearchParams(location.search)]
    .filter(([key]) => key.startsWith('filter.'))
    .map(([key, value]) => `${key}=${value}`)
    .sort()
    .join('&');
}

/**
 * Customer identity travels only with analytics consent. Shopify's Customer
 * Privacy API is loaded by snippets/cookiebot.liquid; when it is not there
 * yet, or consent is missing, the pageview goes out without identity.
 * @returns {boolean}
 */
function identityAllowed() {
  const privacy = window.Shopify?.customerPrivacy;
  return typeof privacy?.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed() === true;
}

/** @type {string | null} */
let pushedFiltersKey = null;

// Published on load and again after a PLP facet change (a soft navigation:
// the section re-renders in place and pushState runs before `filter:update`).
// The same facet set is never pushed twice, so sort changes and search-page
// facet clicks do not inflate pageviews.
function pushPageview() {
  const filtersKey = activeFiltersKey();
  if (filtersKey === pushedFiltersKey) return;
  pushedFiltersKey = filtersKey;

  const { customer, labels, ...context } = pageContext();
  const pageType =
    context.page_type === 'product listing' && filtersKey ? 'filtered product listing' : context.page_type;
  push({
    event: 'pageview',
    page_location: location.pathname + location.search,
    ...context,
    page_type: pageType || 'other',
    store_country: 'it',
    responsive_layout: responsiveLayout(),
    ...(customer && identityAllowed() ? customer : {}),
  });
}

pushPageview();
document.addEventListener(ThemeEvents.FilterUpdate, pushPageview);

/* --------------------------------- items ---------------------------------- */

/**
 * The PDP variant -> item map rendered by sections/product-information.liquid.
 * The quick-add dialog clones the PDP content, so exclude the dialog's copy
 * when looking at the whole document.
 * @param {ParentNode} [scope]
 * @returns {HTMLElement | null}
 */
function itemsMapElement(scope = document) {
  return (
    /** @type {HTMLElement[]} */ ([...scope.querySelectorAll('[data-analytics-items]')]).find(
      (element) => scope !== document || !element.closest('#quick-add-dialog'),
    ) || null
  );
}

/**
 * @param {HTMLElement | null} mapElement
 * @param {string | number | undefined} variantId
 * @returns {Record<string, unknown> | null}
 */
function itemFromMap(mapElement, variantId) {
  if (!mapElement || variantId == null) return null;
  return parseJSON(mapElement.textContent)?.[String(variantId)] || null;
}

/**
 * @param {string} name
 * @param {Record<string, unknown>[]} items
 * @param {Record<string, unknown>} [extra]
 */
function pushEcommerce(name, items, extra = {}) {
  if (items.length === 0) return;
  push({ event: name, ecommerce: { items }, ...extra });
}

/* -------------------------------- view_item ------------------------------- */

{
  const mapElement = itemsMapElement();
  const item = itemFromMap(mapElement, mapElement?.dataset.selectedVariantId);
  if (item) pushEcommerce('view_item', [item]);
}

// Capture phase: product-card.js stops the event's propagation at the card,
// so a bubble listener on document never sees card swatch changes.
document.addEventListener(
  ThemeEvents.variantUpdate,
  (event) => {
    const { target } = event;
    const detail = /** @type {{ detail?: { resource?: { id?: number }, data?: { html?: Document } } }} */ (event)
      .detail;
    if (!(target instanceof Element)) return;

    // A card swatch change morphs only the picker, price and links: refresh the
    // card's item from the fetched card markup so an in-card add reports the
    // variant actually added.
    const card = target.closest('product-card');
    if (card) {
      const fresh = detail?.data?.html?.querySelector?.('product-card[data-analytics-item]');
      if (fresh) card.setAttribute('data-analytics-item', fresh.getAttribute('data-analytics-item') || '');
      return;
    }
    if (target.closest('#quick-add-dialog')) return;

    // The PDP section was morphed before this event fired, so the map is fresh.
    // Its selected id is not (contact-lens PDPs morph only the picker): keep it
    // on the variant now picked, which the lens configurator's line item and
    // add_to_cart below read (VIS-1081).
    const mapElement = itemsMapElement();
    if (mapElement && detail?.resource?.id) mapElement.dataset.selectedVariantId = String(detail.resource.id);
    const item = itemFromMap(mapElement, detail?.resource?.id);
    if (item) pushEcommerce('view_item', [item]);
  },
  true,
);

/* ------------------------------- add_to_cart ------------------------------ */

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const { sourceId, data } =
    /** @type {{ detail: { sourceId?: string, data?: Record<string, any> } }} */ (event).detail || {};
  const { target } = event;
  if (!data || data.didError || !(target instanceof Element)) return;

  if (data.source === 'lens-configurator') {
    // The child lens SKUs are unlisted products with no Liquid item JSON: the
    // master pack item carries the categories, each line (from the
    // /cart/add.js response) overrides identity, price and quantity.
    const mapElement = itemsMapElement();
    const master = itemFromMap(mapElement, mapElement?.dataset.selectedVariantId);
    if (!master) return;
    const lines = Array.isArray(data.items) ? data.items : [];
    pushEcommerce(
      'add_to_cart',
      lines.map((line) => ({
        ...master,
        item_variant_id: line.sku || String(line.variantId),
        price: line.price ?? master.price,
        quantity: line.quantity ?? 1,
        ...listNameFrom(target),
      })),
    );
    return;
  }

  if (data.source !== 'product-form-component') return;

  const scope = target.closest('#quick-add-dialog') || target.closest('product-card') || document;
  const mapElement = itemsMapElement(scope);
  // Single adds carry the variant id as sourceId; the queued batch path
  // (submit during a variant change) carries the lines in data.items.
  const lines = Array.isArray(data.items)
    ? data.items
    : [{ variantId: sourceId, quantity: Number(data.itemCount) || 1 }];
  const items = lines
    .map((line) => {
      let item = itemFromMap(mapElement, line.variantId);
      if (!item && scope instanceof HTMLElement) item = parseJSON(scope.dataset.analyticsItem);
      return item && { ...item, quantity: line.quantity, ...listNameFrom(target) };
    })
    .filter(Boolean);
  pushEcommerce('add_to_cart', items);
});

/**
 * The list the product was picked from, as stamped on the form.
 * @param {Element} form
 * @returns {{ item_list_name?: string }}
 */
function listNameFrom(form) {
  const input = form.querySelector(`input[name="${LIST_PROPERTY}"]`);
  return input instanceof HTMLInputElement && input.value ? { item_list_name: input.value } : {};
}

/* ------------------------ view_cart / cart quantity ----------------------- */

/**
 * A cart row (snippets/cart-products.liquid) as a GA4 item: the Liquid item on
 * the row with the money from the line. Same rule as the checkout pixel:
 * price is what the shopper pays per unit, discount the list price minus that.
 * @param {HTMLElement} row
 * @param {{ final_price?: number, quantity?: number }} [line] - The fresh
 *   /cart/change.js line, when the row is about to be re-rendered.
 * @returns {Record<string, unknown> | null}
 */
function itemFromCartRow(row, line = {}) {
  const item = parseJSON(row.dataset.analyticsItem);
  if (!item) return null;
  const paid = Number(line.final_price ?? row.dataset.finalPrice) / 100;
  let list = Number(item.price || 0) + Number(item.discount || 0);
  if (list <= paid) list = Math.max(paid, Number(row.dataset.originalPrice) / 100);
  const discount = Math.round((list - paid) * 100) / 100;
  return {
    ...item,
    price: paid,
    discount,
    item_discounted: discount > 0,
    quantity: line.quantity ?? Number(row.dataset.quantity),
    ...(row.dataset.itemListName ? { item_list_name: row.dataset.itemListName } : {}),
  };
}

/** @param {ParentNode} scope - The cart page or the drawer. */
function pushViewCart(scope) {
  const items = /** @type {HTMLElement[]} */ ([...scope.querySelectorAll('tr[data-analytics-item]')])
    .map((row) => itemFromCartRow(row))
    .filter((item) => item !== null);
  if (items.length === 0) return;
  const value = items.reduce((sum, item) => sum + Number(item.price) * Number(item.quantity), 0);
  push({
    event: 'view_cart',
    ecommerce: { currency: items[0].currency || 'EUR', value: Math.round(value * 100) / 100, items },
  });
}

if (pageContext().page_type === 'cart') pushViewCart(document);

// The drawer opens inside requestAnimationFrame, after the add's section morph,
// so an auto-open following an add already sees the new line. dialog:open does
// not bubble: listen in capture phase.
document.addEventListener(
  'dialog:open',
  (event) => {
    if (event.target instanceof Element && event.target.matches('cart-drawer-component')) pushViewCart(event.target);
  },
  true,
);

// A quantity raised in the cart ("+" or typed) is an add of the difference.
// cart-items-component dispatches before it morphs, so the rows still carry
// the old quantity. Lines are matched by position and variant, not by key:
// the key's hash changes with the quantity on this store. Decreases push
// nothing: remove_from_cart is not in the SDD.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const { resource, data } =
    /** @type {{ detail: { resource?: { items?: { variant_id: number, quantity: number, final_price: number }[] }, data?: Record<string, any> } }} */ (
      event
    ).detail || {};
  const scope = event.target;
  if (data?.source !== 'cart-items-component' || data.didError || !(scope instanceof Element)) return;

  const rows = /** @type {HTMLElement[]} */ ([...scope.querySelectorAll('tr[data-key]')]);
  const items = (resource?.items || [])
    .map((line, index) => {
      const row = rows[index];
      if (row?.dataset.key?.split(':')[0] !== String(line.variant_id)) return null;
      const added = line.quantity - Number(row.dataset.quantity);
      return added > 0 ? itemFromCartRow(row, { final_price: line.final_price, quantity: added }) : null;
    })
    .filter((item) => item !== null);
  pushEcommerce('add_to_cart', items);
});

/* --------------------- line item property propagation --------------------- */

/**
 * Creates, refreshes or drops a hidden line item property on a product form.
 * The PDP form node survives the `main` morph when the card colour picker
 * swaps the product, so a stamp left over from the previous product has to go
 * rather than stay.
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string | null | undefined} value
 */
function stampProperty(form, name, value) {
  let input = form.querySelector(`input[name="${name}"]`);
  if (!value) {
    input?.remove();
    return;
  }
  if (!(input instanceof HTMLInputElement)) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.append(input);
  }
  input.value = value;
}

// A card click remembers the list the product was picked from (sessionStorage
// via @theme/utilities, keyed by product id) rather than stamping a URL param:
// a param fragmented `page_location` and invalidated quick-add's prefetched
// PDP URL. The product form, the quick-add dialog and the LAC configurator
// read the same key.
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('product-card');
    const list = card?.closest('[data-item-list-name]')?.getAttribute('data-item-list-name');
    if (card instanceof HTMLElement && list) rememberItemList(card.dataset.productId, list);
  },
  true,
);

// On `window`, capture phase: the Component framework dispatches `on:submit`
// handlers from a document-level capture listener registered at load
// (component.js), and product-form.js builds its FormData right there. The
// window capture target comes first in the event path, so the inputs exist
// by the time the form is read.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const component = form.closest('product-form-component');
    if (!(component instanceof HTMLElement)) return;

    const list =
      form.closest('[data-item-list-name]')?.getAttribute('data-item-list-name') ||
      rememberedItemList(component.dataset.productId);
    stampProperty(form, LIST_PROPERTY, list);

    // The whole item travels with the line so the checkout pixel can report
    // begin_checkout and purchase with the same fields the storefront events
    // carry: checkout has no access to the Liquid mapping, and the pixel
    // sandbox cannot read the page. Volatile fields (price, quantity,
    // discount) are overridden pixel-side from the checkout line.
    const scope = form.closest('#quick-add-dialog') || form.closest('product-card') || document;
    // The queue in product-form.js resolves the intended variant from the URL,
    // which variant-picker.js updates as soon as the section fetch starts,
    // while `input[name="id"]` only catches up on `variantUpdate`. Read the
    // same source so a submit mid-change stamps the variant that is queued.
    // (Two swatches clicked before the first update lands still share one
    // form property; splitting that needs per-item properties in the queue.)
    const variantInput = form.querySelector('input[name="id"]');
    const variantId =
      (scope === document ? new URL(location.href).searchParams.get('variant') : null) ||
      (variantInput instanceof HTMLInputElement ? variantInput.value : undefined);
    const item =
      itemFromMap(itemsMapElement(scope), variantId) ||
      (scope instanceof HTMLElement ? parseJSON(scope.dataset.analyticsItem) : null);
    stampProperty(form, ITEM_PROPERTY, item && JSON.stringify(item));
  },
  true,
);

/* ------------------------------ view_item_list ---------------------------- */

// An impression is a card that actually entered the viewport (half of it, the
// GA4 convention), not a card that entered the DOM: carousels below the fold
// and later pages of a list are reported only when the shopper scrolls to
// them, so the list CTR stays meaningful. Cards are picked up by a
// MutationObserver as they render (initial page, load more, filter re-render,
// lazy recommendations, predictive search) and handed to one
// IntersectionObserver; visible cards are flushed in batches, grouped by list
// container and split into pushes of ten. The section morph strips the
// markers from kept cards on a filter re-render, so a re-render counts as a
// new list view on purpose. debounce rather than requestAnimationFrame: rAF
// is suspended in background tabs.

/** @type {Set<Element>} */
const visibleCards = new Set();

function flushLists() {
  /** @type {Map<Element, Record<string, unknown>[]>} */
  const groups = new Map();
  /** @type {Map<Element, Element[]>} */
  const positions = new Map();

  for (const card of visibleCards) {
    if (!card.isConnected || card.hasAttribute('data-analytics-viewed')) continue;
    card.setAttribute('data-analytics-viewed', '');
    const container = card.closest('[data-item-list-name]');
    const item = container && parseJSON(card.getAttribute('data-analytics-item'));
    if (!container || !item) continue;

    const cards = positions.get(container) || [...container.querySelectorAll('product-card[data-analytics-item]')];
    positions.set(container, cards);
    const gridIndex = Number(card.closest('[data-grid-index]')?.getAttribute('data-grid-index'));
    const index = gridIndex > 0 ? gridIndex : cards.indexOf(card) + 1;

    const items = groups.get(container) || [];
    items.push({
      ...item,
      item_list_name: container.getAttribute('data-item-list-name'),
      index,
    });
    groups.set(container, items);
  }
  visibleCards.clear();

  for (const [container, items] of groups) {
    const filters = parseJSON(container.getAttribute('data-analytics-filters')) || [];
    for (let start = 0; start < items.length; start += BATCH_SIZE) {
      pushEcommerce('view_item_list', items.slice(start, start + BATCH_SIZE), { filters });
    }
  }
}

const scheduleFlush = debounce(flushLists, FLUSH_DELAY_MS);

const viewportObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      viewportObserver.unobserve(entry.target);
      visibleCards.add(entry.target);
    }
    if (visibleCards.size > 0) scheduleFlush();
  },
  { threshold: 0.5 },
);

// Cards outside a list container are never impressions; they are marked so
// the scan stays cheap.
function observeNewCards() {
  for (const card of document.querySelectorAll(
    'product-card[data-analytics-item]:not([data-analytics-viewed]):not([data-analytics-observed])',
  )) {
    card.setAttribute('data-analytics-observed', '');
    if (card.closest('[data-item-list-name]')) viewportObserver.observe(card);
  }
}

const scheduleObserve = debounce(observeNewCards, FLUSH_DELAY_MS);
new MutationObserver(scheduleObserve).observe(document.body, {
  childList: true,
  subtree: true,
});
observeNewCards();

/* --------------------------- store events (5, 6) -------------------------- */

/**
 * The store identity rendered on the locator card and on the store-detail
 * section root (snippets/analytics-store.liquid).
 * @param {Element} element
 * @returns {Record<string, unknown> | null}
 */
function storeFrom(element) {
  return parseJSON(element.closest('[data-analytics-store]')?.getAttribute('data-analytics-store'));
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.checked) return;
  if (!input.matches('store-favorite-toggle [data-favorite-toggle]')) return;
  const store = storeFrom(input);
  if (!store) return;

  let position = 'Store page';
  if (input.closest('store-selector, #store-selector-modal')) position = 'Store modal';
  else if (input.closest('store-locator')) position = 'Store list';

  push({
    event: 'preferred_store_set',
    ...store,
    logged_in: pageContext().login_status === 'logged',
    position,
  });
});

/**
 * The locator facets active at click time, in the wording of the filter UI
 * (labels come from the locale file through Theme.analytics.labels).
 * @returns {{ type: string, value: string }[]}
 */
function locatorFilters() {
  const locator = /** @type {{ filterState?: Record<string, any> } | null} */ (document.querySelector('store-locator'));
  const state = locator?.filterState;
  const labels = pageContext().labels || {};
  if (!state) return [];
  const filters = [];
  if (state.q) filters.push({ type: labels.search, value: state.q });
  if (state.region) filters.push({ type: labels.region, value: state.region });
  for (const key of state.openings || [])
    filters.push({
      type: labels.openings,
      value: labels[`opening_${key}`] || key,
    });
  for (const key of state.flags || [])
    filters.push({
      type: labels.services,
      value: labels[`flag_${key}`] || key,
    });
  if (state.mode === 'radius' && state.radius) filters.push({ type: labels.radius, value: `${state.radius} km` });
  return filters;
}

const APPOINTMENT_CTA =
  '[data-store-card] .store-card__cta, [data-store-detail-hero] .store-detail-hero__cta-link--primary, .store-detail__service-cta, book-appointment-cta a, a[data-book-appointment]';

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const cta = event.target.closest(APPOINTMENT_CTA);
  if (!cta) return;

  let store = storeFrom(cta);
  if (!store && cta.closest('book-appointment-cta, [data-book-appointment]')) {
    // The global CTAs route to the favorite store; without one they open the
    // store selector or the locator instead of booking, so there is nothing to
    // attribute.
    const favorite = getFavorite();
    if (!favorite) return;
    store = {
      store_id: favorite.id || favorite.handle,
      store_name: favorite.name,
      store_city: favorite.address || null,
    };
  }
  if (!store) return;

  rememberBookingStore(cta, store);
  push({
    event: 'book_appointment',
    step: 'step_0',
    ...store,
    // Where the booking happens: our request form (visionottica.it) or the
    // Salesforce scheduler (visiongroupcore.my.site.com). Storeis cannot read
    // it from GTM's Click URL, so it travels on the event.
    site_appointment:
      cta instanceof HTMLAnchorElement ? new URL(cta.href, location.origin).hostname.replace(/^www\./, '') : null,
    filters: cta.closest('store-locator') ? locatorFilters() : [],
  });
});

/* ------------------------ book_appointment (step 3) ----------------------- */

// Stores without a Salesforce scheduler book through the Shopify Forms request
// form (/pages/richiesta-appuntamento?store=<handle>, VIS-310). The scheduler's
// own confirmation runs on Salesforce, out of the theme's reach, so step 3
// exists only for this form. Store fields travel from the step 0 click in
// sessionStorage, keyed by the handle the CTA passes.
const BOOKING_FORM_PATH = '/pages/richiesta-appuntamento';
const BOOKING_STORE_KEY = 'analytics:booking-store';

/**
 * @param {Element} cta
 * @param {Record<string, unknown>} store
 */
function rememberBookingStore(cta, store) {
  if (!(cta instanceof HTMLAnchorElement)) return;
  const url = new URL(cta.href, location.origin);
  if (!url.pathname.endsWith(BOOKING_FORM_PATH)) return;
  try {
    sessionStorage.setItem(BOOKING_STORE_KEY, JSON.stringify({ handle: url.searchParams.get('store'), store }));
  } catch {
    // Private mode / quota: step 3 goes out without the store.
  }
}

/** @returns {Record<string, unknown>} The store the form was opened for, or null fields. */
function bookingStore() {
  let remembered = null;
  try {
    remembered = parseJSON(sessionStorage.getItem(BOOKING_STORE_KEY));
  } catch {
    // Storage blocked.
  }
  const handle = new URLSearchParams(location.search).get('store');
  if (remembered?.handle && remembered.handle === handle) return remembered.store;
  return { store_id: null, store_name: null, store_city: null };
}

/**
 * @param {string} value
 * @returns {Promise<string>}
 */
async function sha256Hex(value) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * The fields step 3 needs, read at submit time. The form lives in the embed's
 * open shadow root; the service is a custom listbox, so its label is read from
 * the toggle's value span.
 * @param {ShadowRoot} root
 */
function bookingValues(root) {
  const text = (/** @type {string} */ selector) => {
    const element = root.querySelector(selector);
    if (element instanceof HTMLInputElement) return element.value.trim();
    return element?.textContent?.trim() || '';
  };
  // The phone input carries the country prefix itself ("+39 333 …"): keep
  // digits and the leading +, and drop a bare prefix nobody completed.
  const phone = text('input[type="tel"]').replace(/[^\d+]/g, '');
  return {
    email: text('input#email').toLowerCase(),
    phone: /\d{6}/.test(phone) ? phone : '',
    service: text('[id*="interested_in"][id$="-selector-value"]'),
  };
}

/** @param {{ email: string, phone: string, service: string }} values */
async function pushBookingConfirmed(values) {
  const identity = {};
  if (identityAllowed() && (values.email || values.phone)) {
    if (values.email) identity.email = await sha256Hex(values.email);
    if (values.phone) identity.numberSHA256 = await sha256Hex(values.phone);
    identity.hash_method = 'SHA256';
  }
  push({
    event: 'book_appointment',
    step: 'step_3',
    ...bookingStore(),
    service_type: values.service || null,
    ...identity,
  });
}

// The embed mounts asynchronously, so poll for it like appointment-form-prefill.js.
// Success is the embed root switching `data-current-step` to "success"; a
// submit that fails validation or errors never gets there.
if (location.pathname.endsWith(BOOKING_FORM_PATH)) {
  const started = Date.now();
  const timer = setInterval(() => {
    const root = document.querySelector('shopify-forms-embed')?.shadowRoot;
    if (!root?.querySelector('form')) {
      if (Date.now() - started > 30000) clearInterval(timer);
      return;
    }
    clearInterval(timer);

    /** @type {{ email: string, phone: string, service: string } | null} */
    let submitted = null;
    root.addEventListener('submit', () => (submitted = bookingValues(root)), true);
    const observer = new MutationObserver(() => {
      if (!submitted || !root.querySelector('[data-current-step="success"]')) return;
      observer.disconnect();
      pushBookingConfirmed(submitted);
    });
    observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-current-step'] });
  }, 250);
}
